import { FormControl, Grid, type TextFieldProps } from "@mui/material";
import { ReactComponent as IconAUT } from "Components/_Shared/Icons/Countries/AUT.svg";
import { ReactComponent as IconCZE } from "Components/_Shared/Icons/Countries/CZE.svg";
import { ReactComponent as IconDEU } from "Components/_Shared/Icons/Countries/DEU.svg";
import { ReactComponent as IconPOL } from "Components/_Shared/Icons/Countries/POL.svg";
import { ReactComponent as IconsSVK } from "Components/_Shared/Icons/Countries/SVK.svg";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { useListPhonePrefixes } from "Infrastructure/ApiZfp/AnalyzerApi";
import { Spacing } from "Infrastructure/Themes/Themes";
import type { JSX } from "react";
import { type Control, Controller, type FieldErrors, type FieldPath, type FieldValues } from "react-hook-form";
import { BlDropdown } from "./BlDropdown";

type Props<FormType extends FieldValues> = Omit<TextFieldProps, "onChange"> & {
  readonly prefixInputName: FieldPath<FormType>;
  readonly numberInputName: FieldPath<FormType>;
  readonly control: Control<FormType, object>;
  readonly errors: FieldErrors<FormType>;
  readonly label?: string;
  readonly labelEndAdornment?: JSX.Element;
  readonly disabled?: boolean;
};

export const PhoneInput = <T extends object>({
  label,
  prefixInputName,
  numberInputName,
  control,
  labelEndAdornment,
  errors,
  disabled,
}: Props<T>) => {
  const { data } = useListPhonePrefixes();

  const getIcon = (value: string) => {
    switch (value) {
      case "+420":
        return <IconCZE />;
      case "+421":
        return <IconsSVK />;
      case "+48":
        return <IconPOL />;
      case "+49":
        return <IconDEU />;
      case "+43":
        return <IconAUT />;
    }
  };

  return (
    <FormControl fullWidth>
      <Grid container spacing={Spacing.L} alignItems="flex-end">
        <Grid size={6.2}>
          <Controller
            control={control}
            name={prefixInputName}
            render={({ field: { onChange, value } }) => {
              return (
                <BlDropdown
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                  disabled={disabled}
                  value={value}
                  options={
                    data?.map((item) => ({
                      Name: `${item.code} ${item.prefix}`,
                      Value: item.prefix,
                    })) || [
                      {
                        Name: "",
                        Value: "+420",
                      },
                    ]
                  }
                  name="Country code"
                  label={label}
                  labelEndAdornment={labelEndAdornment}
                  onChange={onChange}
                  icon={getIcon(value)}
                />
              );
            }}
          />
        </Grid>
        <Grid size={5.8}>
          <BlFormInput
            control={control}
            name={numberInputName}
            variant="outlined"
            fullWidth
            errors={errors}
            disabled={disabled}
            mask="000 000 000"
          />
        </Grid>
      </Grid>
    </FormControl>
  );
};
